/**
 * Phase 10 R5 — FinancialSnapshotCard
 *
 * Lightweight Dashboard surface showing today's (or selected day's) gross
 * profit health. Uses `useDailyFinancialSnapshot`, which guarantees a
 * 4-query batched fetch regardless of ticket count.
 *
 * Self-hides when:
 *   - feature flag off (`ui_ticket_financials`)
 *   - role/visibility-mode denies (TECH always denied; SW per shop config)
 *   - shop has no tickets for the date
 *
 * UI-only. Never persists. Never aggregates server-side.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertOctagon,
  AlertTriangle,
  TrendingUp,
  ChevronRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDailyFinancialSnapshot } from '@/hooks/labor/useDailyFinancialSnapshot';

// DCC-A4: dropped `format` from date-fns import + replaced
// `selectedDate: Date` prop with canonical `selectedDayLabel: string`.
// Pre-DCC-A4 the prop was a Date that this widget ONLY used to
// derive a YYYY-MM-DD query key for `useDailyFinancialSnapshot`. The
// Date was never used for display in this component. The canonical
// SoT is `selectedDayLabel` (DateContext doc-block: "Do NOT use this
// for database queries" for the Date object).

const fmt = (n: number) =>
  n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
const fmtCents = (n: number) =>
  n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });

interface Props {
  selectedDayLabel: string;
}

export function FinancialSnapshotCard({ selectedDayLabel }: Props) {
  // DCC-A4: pass canonical SoT directly to the hook (no derivation).
  const { snapshot, isLoading, enabled } = useDailyFinancialSnapshot(selectedDayLabel);
  const navigate = useNavigate();

  if (!enabled) return null;

  if (isLoading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Financial Snapshot
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Loading…
        </CardContent>
      </Card>
    );
  }

  if (!snapshot || snapshot.ticket_count === 0) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Financial Snapshot
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          No tickets scheduled for this day.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Financial Snapshot
          </CardTitle>
          <Badge variant="outline" className="text-[10px]">
            Gross profit · not net
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {/* Sold totals */}
        <div className="grid grid-cols-2 gap-2">
          <Stat label="Parts sold" value={fmt(snapshot.parts.sell)} />
          <Stat label="Labor sold" value={fmt(snapshot.laborSell)} />
        </div>

        {/* Combined GP — emphasized */}
        <div className="rounded-md border bg-muted/30 p-3 space-y-1">
          <div className="flex items-center justify-between text-xs uppercase tracking-wide text-muted-foreground">
            <span>Combined GP</span>
            <span>{snapshot.combined.gpPercent.toFixed(1)}%</span>
          </div>
          <div
            className={`text-xl font-semibold tabular-nums ${
              snapshot.combined.gpAmount < 0 ? 'text-destructive' : ''
            }`}
          >
            {fmt(snapshot.combined.gpAmount)}
          </div>
        </div>

        {/* Warning counts */}
        {(snapshot.low_margin_ticket_count > 0 ||
          snapshot.negative_gp_ticket_count > 0) && (
          <div className="space-y-1">
            {snapshot.negative_gp_ticket_count > 0 && (
              <WarningLine
                icon={<AlertOctagon className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />}
                tone="critical"
                text={`${snapshot.negative_gp_ticket_count} ticket${
                  snapshot.negative_gp_ticket_count === 1 ? '' : 's'
                } with negative gross profit`}
              />
            )}
            {snapshot.low_margin_ticket_count > 0 && (
              <WarningLine
                icon={<AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0 mt-0.5" />}
                tone="warn"
                text={`${snapshot.low_margin_ticket_count} ticket${
                  snapshot.low_margin_ticket_count === 1 ? '' : 's'
                } below target margin`}
              />
            )}
          </div>
        )}

        {/* Top 3 lowest GP */}
        {snapshot.lowest_gp_tickets.length > 0 && (
          <div className="space-y-1 border-t pt-2">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Lowest GP today
            </div>
            {snapshot.lowest_gp_tickets.map((t) => (
              <button
                key={t.ticket_id}
                onClick={() =>
                  navigate(`/?tab=dayboard&highlight=${t.ticket_id}&openDetails=1`)
                }
                className="w-full flex items-center justify-between gap-2 text-xs rounded px-2 py-1 hover:bg-muted/50 transition-colors"
              >
                <span className="font-mono text-muted-foreground">
                  {t.ticket_number ? `#${t.ticket_number}` : t.ticket_id.slice(0, 6)}
                </span>
                <span
                  className={`tabular-nums ${
                    t.gpAmount < 0 ? 'text-destructive font-medium' : ''
                  }`}
                >
                  {fmtCents(t.gpAmount)}
                </span>
                <span className="tabular-nums text-muted-foreground w-12 text-right">
                  {t.gpPercent.toFixed(0)}%
                </span>
                <ChevronRight className="h-3 w-3 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}

        {/* Footer disclosure */}
        <p className="text-[10px] text-muted-foreground border-t pt-2 leading-tight">
          Based on {snapshot.ticket_count} ticket
          {snapshot.ticket_count === 1 ? '' : 's'} ·{' '}
          {snapshot.partial_ticket_count} partial ·{' '}
          {snapshot.insufficient_ticket_count} excluded due to insufficient data.
          Excludes taxes, fees, overhead.
        </p>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-0.5">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function WarningLine({
  icon,
  text,
  tone,
}: {
  icon: React.ReactNode;
  text: string;
  tone: 'warn' | 'critical';
}) {
  const toneClass = tone === 'critical' ? 'text-destructive' : 'text-warning';
  return (
    <div className={`flex items-start gap-2 text-xs ${toneClass}`}>
      {icon}
      <span>{text}</span>
    </div>
  );
}
